import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import HamburgerMenu from './global/menu';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import Welcome from './onboarding/welcome';
import Signup from './onboarding/signup';
import EmailConfirmation from './onboarding/email-confirmation';
import Login from './onboarding/login';
import AuthCallback from './onboarding/AuthCallback';
import CompleteProfile from './onboarding/CompleteProfile';
import SetPin from './onboarding/set-pin';
import ForgotPassword from './onboarding/forgot-password';
import PasswordReset from './onboarding/password-reset';
import DashboardHome from './dashboard/home';
import Admin from './dashboard/admin';

// Import Dashboard Components
import DailyDashboard from './dashboard/daily';
import PatientCareGuidelines from './dashboard/patientCareGuidelines';
import PatientWeightConverter from './dashboard/patientWeightConverter';
import IVMedicationCalculator from './dashboard/ivMedicationCalculator';
import GlasgowComaScale from './dashboard/glasgowComaScale';
import ApgarCalculator from './dashboard/apgarCalculator';
import HamiltonT1Duration from './dashboard/hamiltonT1Duration';
import PhoneDirectory from './dashboard/phoneDirectory';
import PublicDailyStatus from './dashboard/publicDailyStatus';
import UpdateDailyStatus from './dashboard/updateDailyStatus';

// Import Admin-only Components
import ChainOfCustodyReport from './dashboard/chainOfCustodyReport';
import ProviderUsageReport from './dashboard/providerUsageReport';
import MasterInventory from './dashboard/masterInventory';
import DailyDashboardAdmin from './dashboard/dailyDashboardAdmin';
import MileageReport from './dashboard/mileageReport';
import ExpirationReport from './dashboard/expirationReport';
import TruckActivityReport from './dashboard/truckActivityReport';
import ShiftReconciliationReport from './dashboard/shiftReconciliationReport';
import Users from './dashboard/users';
import NonControlledReport from './dashboard/nonControlledReport';

// Import Location Components
import Mayo21 from './locations/mayo21';
import Mayo22 from './locations/mayo22';
import Mayo23 from './locations/mayo23';
import Mayo24 from './locations/mayo24';
import Mayo25 from './locations/mayo25';
import EMSStation from './locations/emsStation';
import EMSExpired from './locations/emsExpired';
import DailyTruck from './locations/dailyTruck';
import TruckCheckOut from './locations/truckCheckOut';
import NonControlledMedications from './locations/nonControlledMedications';

import { authHelpers } from './global/supabase';
import './App.css';

// Profile Component
function Profile({ userInfo, isAdmin, onLogout, onNavigate }) {
  const { refreshProfile } = useAuth();
  const navigate = useNavigate();

  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [editingData, setEditingData] = useState({
    firstName: '',
    lastName: '',
    stateLicenseNumber: '',
    pin: ''
  });
  const [emailChangeData, setEmailChangeData] = useState({
    newEmail: '',
    isProcessing: false,
    pendingEmail: '',
    status: '',
    message: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    isProcessing: false,
    status: '',
    message: '',
    showCurrentPassword: false,
    showNewPassword: false
  });

  // Update editing data when userInfo changes
  useEffect(() => {
    if (userInfo) {
      setEditingData({
        firstName: userInfo.firstName || '',
        lastName: userInfo.lastName || '',
        stateLicenseNumber: userInfo.stateLicenseNumber || '',
        pin: ''
      });
    }
  }, [userInfo]);

  // Detect mobile device for PWA install button
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    setIsMobile(isIOS || isAndroid);
  }, []);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const { supabase } = await import('./global/supabase');

      if (editingData.pin && !/^\d{6}$/.test(editingData.pin)) {
        alert('PIN must be exactly 6 digits.');
        return;
      }

      // Own-row edits go through the RPC (names and license only); the PIN
      // goes only to set_own_pin, which hashes it.
      const { error } = await supabase.rpc('update_user_metadata', {
        user_id: userInfo.id,
        first_name: editingData.firstName,
        last_name: editingData.lastName,
        state_license_number: editingData.stateLicenseNumber,
      });
      if (error) {
        throw error;
      }

      if (editingData.pin) {
        const { error: pinError } = await supabase.rpc('set_own_pin', { new_pin: editingData.pin });
        if (pinError) {
          alert(pinError.message || 'Could not save the new PIN.');
          return;
        }
      }

      // Refresh profile from context
      await refreshProfile();

      setIsEditing(false);
      console.log('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailChange = async () => {
    if (!emailChangeData.newEmail.trim()) return;

    setEmailChangeData(prev => ({
      ...prev,
      isProcessing: true,
      status: '',
      message: ''
    }));

    try {
      const { authHelpers } = await import('./global/supabase');

      const { error } = await authHelpers.supabase.auth.updateUser({
        email: emailChangeData.newEmail
      });

      if (error) {
        throw error;
      }

      setEmailChangeData(prev => ({
        ...prev,
        isProcessing: false,
        pendingEmail: prev.newEmail,
        status: 'sent',
        message: `Confirmation email sent to ${prev.newEmail}`,
        newEmail: ''
      }));
    } catch (error) {
      console.error('Email change error:', error);
      setEmailChangeData(prev => ({
        ...prev,
        isProcessing: false,
        status: 'error',
        message: error.message || 'Failed to send confirmation email. Please try again.'
      }));
    }
  };

  const handleResendEmail = async () => {
    setEmailChangeData(prev => ({
      ...prev,
      newEmail: prev.pendingEmail
    }));
    await handleEmailChange();
  };

  const handleChangeEmailAddress = () => {
    setEmailChangeData(prev => ({
      ...prev,
      status: '',
      message: '',
      pendingEmail: '',
      newEmail: ''
    }));
  };

  const handlePasswordChange = async () => {
    if (!passwordChangeData.newPassword.trim()) {
      setPasswordChangeData(prev => ({ ...prev, status: 'error', message: 'New password is required.' }));
      return;
    }
    if (passwordChangeData.newPassword.length < 6) {
      setPasswordChangeData(prev => ({ ...prev, status: 'error', message: 'Password must be at least 6 characters.' }));
      return;
    }
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      setPasswordChangeData(prev => ({ ...prev, status: 'error', message: 'Passwords do not match.' }));
      return;
    }
    setPasswordChangeData(prev => ({ ...prev, isProcessing: true, status: '', message: '' }));
    try {
      const { authHelpers } = await import('./global/supabase');
      const { error } = await authHelpers.supabase.auth.updateUser({ password: passwordChangeData.newPassword });
      if (error) throw error;
      setPasswordChangeData({ currentPassword: '', newPassword: '', confirmPassword: '', isProcessing: false, status: 'success', message: 'Password updated successfully!', showCurrentPassword: false, showNewPassword: false });
    } catch (error) {
      setPasswordChangeData(prev => ({ ...prev, isProcessing: false, status: 'error', message: error.message || 'Failed to update password.' }));
    }
  };

  return (
    <div className="container-center">
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="card card-center" style={{ maxWidth: '500px', width: '100%' }}>
        <h1 className="title">Profile Settings</h1>

        {/* Subtle App Install Link - Mobile Only */}
        {isMobile && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button
              onClick={() => setShowPWAPrompt(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                fontSize: '0.9em',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              Install as App
            </button>
          </div>
        )}

        {/* Basic Profile Information */}
        <div style={{ marginBottom: '30px', width: '100%' }}>
          <h3 style={{ marginBottom: '20px', color: '#333' }}>Personal Information</h3>

          {!isEditing ? (
            <div>
              <div style={{ marginBottom: '15px' }}>
                <strong>First Name:</strong> {userInfo?.firstName || 'Not set'}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Last Name:</strong> {userInfo?.lastName || 'Not set'}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>State License Number:</strong> {userInfo?.stateLicenseNumber || 'Not set'}
              </div>
              <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <strong>PIN:</strong>
                <span>{userInfo?.pinResetRequired ? 'Reset required' : '••••••'}</span>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Editor:</strong> {userInfo?.isEditor ? 'Yes' : 'No'}
              </div>
              <div style={{ marginBottom: '20px' }}>
                <strong>Admin:</strong> {isAdmin ? 'Yes' : 'No'}
              </div>
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-primary"
                style={{ marginRight: '10px' }}
              >
                Edit Profile
              </button>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  First Name:
                </label>
                <input
                  type="text"
                  value={editingData.firstName}
                  onChange={(e) => setEditingData(prev => ({ ...prev, firstName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Last Name:
                </label>
                <input
                  type="text"
                  value={editingData.lastName}
                  onChange={(e) => setEditingData(prev => ({ ...prev, lastName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  State License Number:
                </label>
                <input
                  type="text"
                  value={editingData.stateLicenseNumber}
                  onChange={(e) => setEditingData(prev => ({ ...prev, stateLicenseNumber: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  New PIN (6 digits, leave blank to keep the current one):
                </label>
                <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <input
                    type={showPin ? "text" : "password"}
                    value={editingData.pin}
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="new-password"
                    onChange={(e) => setEditingData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
                    style={{
                      width: '100%',
                      padding: '8px 40px 8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '16px',
                      color: '#666'
                    }}
                    title={showPin ? 'Hide PIN' : 'Show PIN'}
                  >
                    {showPin ? '●' : '○'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSaveProfile}
                  className="btn btn-primary"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditingData({
                      firstName: userInfo?.firstName || '',
                      lastName: userInfo?.lastName || '',
                      stateLicenseNumber: userInfo?.stateLicenseNumber || '',
                      pin: ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Email Change Section */}
        <div style={{ marginBottom: '30px', width: '100%', borderTop: '1px solid #eee', paddingTop: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#333' }}>Email Address</h3>

          <div style={{ marginBottom: '20px' }}>
            <strong>Current Email:</strong> {userInfo?.email || 'No email available'}
            {emailChangeData.status === 'success' && (
              <span style={{ color: 'green', marginLeft: '10px' }}>Updated successfully</span>
            )}
          </div>

          {emailChangeData.status === 'sent' && emailChangeData.pendingEmail ? (
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '15px',
              borderRadius: '4px',
              marginBottom: '15px',
              border: '1px solid #bbdefb'
            }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>New Email:</strong> {emailChangeData.pendingEmail}
              </div>
              <div style={{ marginBottom: '10px', color: '#1976d2' }}>
                Status: Waiting for verification...
              </div>
              <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
                Confirmation email sent to {emailChangeData.pendingEmail}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleResendEmail}
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Resend to same email
                </button>
                <button
                  onClick={handleChangeEmailAddress}
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Change email address
                </button>
              </div>
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                New Email Address:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input
                  type="email"
                  value={emailChangeData.newEmail}
                  onChange={(e) => setEmailChangeData(prev => ({ ...prev, newEmail: e.target.value }))}
                  placeholder="Enter new email address"
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleEmailChange}
                  className="btn btn-primary"
                  disabled={emailChangeData.isProcessing || !emailChangeData.newEmail.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {emailChangeData.isProcessing ? 'Sending...' : 'Send Confirmation'}
                </button>
              </div>

              {emailChangeData.status === 'error' && (
                <div style={{
                  color: '#c62828',
                  fontSize: '14px',
                  marginTop: '5px'
                }}>
                  {emailChangeData.message}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Account Actions */}
        <div style={{ marginBottom: '30px', width: '100%', borderTop: '1px solid #eee', paddingTop: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#333' }}>Account Actions</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={onLogout}
              style={{
                padding: '12px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Log Out
            </button>

            <button
              onClick={async () => {
                if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                  try {
                    const { authHelpers } = await import('./global/supabase');
                    const { error } = await authHelpers.supabase.rpc('delete_user_account');

                    if (error) {
                      console.error('Error deleting account:', error);
                      alert('Failed to delete account. Please contact support.');
                      return;
                    }

                    alert('Your account has been deleted.');
                    onLogout();
                  } catch (error) {
                    console.error('Error deleting account:', error);
                    alert('Failed to delete account. Please contact support.');
                  }
                }
              }}
              style={{
                padding: '12px',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Delete Account
            </button>
          </div>
        </div>

        {/* Password Change Section */}
        <div style={{ marginBottom: '30px', width: '100%', borderTop: '1px solid #eee', paddingTop: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#333' }}>Change Password</h3>
          {passwordChangeData.status === 'success' && (
            <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '15px', borderRadius: '4px', marginBottom: '15px', border: '1px solid #c3e6cb' }}>
              {passwordChangeData.message}
            </div>
          )}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>New Password:</label>
            <input type={passwordChangeData.showNewPassword ? "text" : "password"} value={passwordChangeData.newPassword} onChange={(e) => setPasswordChangeData(prev => ({ ...prev, newPassword: e.target.value, status: '', message: '' }))} placeholder="Enter new password" style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Confirm Password:</label>
            <input type="password" value={passwordChangeData.confirmPassword} onChange={(e) => setPasswordChangeData(prev => ({ ...prev, confirmPassword: e.target.value, status: '', message: '' }))} placeholder="Confirm new password" style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }} />
          </div>
          {passwordChangeData.status === 'error' && (
            <div style={{ color: '#d32f2f', fontSize: '14px', marginBottom: '15px' }}>{passwordChangeData.message}</div>
          )}
          <button onClick={handlePasswordChange} className="btn btn-primary" disabled={passwordChangeData.isProcessing || !passwordChangeData.newPassword.trim() || !passwordChangeData.confirmPassword.trim()}>
            {passwordChangeData.isProcessing ? 'Updating...' : 'Change Password'}
          </button>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/home')}
          className="btn btn-secondary btn-full"
        >
          Back to Home
        </button>
      </div>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        isOpen={showPWAPrompt}
        onClose={() => setShowPWAPrompt(false)}
      />
    </div>
  );
}

// Main App Content Component (handles authentication and routing)
function AppContent() {
  const {
    isAuthenticated,
    needsOnboarding,
    needsProfileSetup,
    loading,
    initialized,
    userProfile,
    signOut,
    refreshProfile
  } = useAuth();

  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = userProfile?.isAdmin || false;
  // Every PIN was reset after the 2026-10 incident: nothing else is reachable
  // until the user saves a new one.
  const needsPinReset = isAuthenticated && !needsOnboarding && !needsProfileSetup &&
    userProfile?.pinResetRequired === true;

  useEffect(() => {
    if (!initialized) return;

    const publicRoutes = [
      '/login',
      '/signup',
      '/forgot-password',
      '/password-reset',
      '/confirmation',
      '/onboarding',
      '/auth/callback',
      '/complete-profile',
      '/status'
    ];

    const isPublicRoute = publicRoutes.includes(location.pathname);

    if (location.pathname === '/auth/callback') return;

    const skipOnboardingRoutes = ['/onboarding', '/password-reset', '/forgot-password'];
    if (isAuthenticated && needsOnboarding && !skipOnboardingRoutes.includes(location.pathname)) {
      navigate('/onboarding', { replace: true });
      return;
    }

    if (isAuthenticated && !needsOnboarding && needsProfileSetup && location.pathname !== '/complete-profile') {
      navigate('/complete-profile', { replace: true });
      return;
    }

    if (needsPinReset && location.pathname !== '/set-pin') {
      navigate('/set-pin', { replace: true });
      return;
    }
    if (!needsPinReset && location.pathname === '/set-pin') {
      navigate(isAuthenticated ? '/home' : '/login', { replace: true });
      return;
    }

    if (!isAuthenticated && !isPublicRoute) {
      navigate('/login', { replace: true });
      return;
    }

  const allowedAuthenticatedRoutes = [
  '/auth/callback',
  '/complete-profile',
  '/password-reset',
  '/forgot-password',
  '/status' // allow public daily status for logged-in users
];

if (
  isAuthenticated &&
  !needsOnboarding &&
  !needsProfileSetup &&
  isPublicRoute &&
  !allowedAuthenticatedRoutes.includes(location.pathname)
) {
  navigate('/home', { replace: true });
}
  }, [isAuthenticated, needsOnboarding, needsProfileSetup, needsPinReset, initialized, location.pathname, navigate]);

  if (loading || !initialized) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h2 className="title-auth">Loading...</h2>
        </div>
      </div>
    );
  }

  const handleOnboardingComplete = () => navigate('/login');

  const handleSignupSuccess = (userData) => {
    setUserEmail(userData.user?.email || '');
    navigate('/confirmation');
  };

  const handleEmailConfirmed = () => {};
  const handleGoToLogin = () => navigate('/login');
  const handleSwitchToLogin = () => navigate('/login');
  const handleSwitchToSignup = () => navigate('/signup');

  const handleLoginSuccess = () => navigate('/home');

  const handleProfileComplete = async () => {
    await refreshProfile();
    navigate('/home');
  };

  const handleForgotPassword = () => navigate('/forgot-password');

  const handlePasswordResetRequest = (email) => {
    setUserEmail(email);
    navigate('/password-reset');
  };

  const handlePasswordResetComplete = () => navigate('/login');

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleNavigate = (destination) => {
    const routeMap = {
      home: '/home',
      'daily-dashboard': '/daily-dashboard',
      profile: '/profile',
      'admin-dashboard': '/admin-dashboard',
      'chain-of-custody-report': '/chain-of-custody-report',
      'provider-usage-report': '/provider-usage-report',
      'master-inventory': '/master-inventory',
      'daily-dashboard-admin': '/daily-dashboard-admin',
      'mileage-report': '/mileage-report',
      'expiration-report': '/expiration-report',
      'truck-activity-report': '/truck-activity-report',
      'shift-reconciliation-report': '/shift-reconciliation-report',
      users: '/users',
      'patient-care-guidelines': '/patient-care-guidelines',
      'patient-weight-converter': '/patient-weight-converter',
      'iv-medication-calculator': '/iv-medication-calculator',
      'glasgow-coma-scale': '/glasgow-coma-scale',
      'apgar-calculator': '/apgar-calculator',
      'hamilton-t1-duration': '/hamilton-t1-duration',
      'phone-directory': '/phone-directory',
      'mayo-21': '/locations/mayo21',
      'mayo-22': '/locations/mayo22',
      'mayo-23': '/locations/mayo23',
      'mayo-24': '/locations/mayo24',
      'mayo-25': '/locations/mayo25',
      'ems-station': '/locations/ems-station',
      'ems-expired': '/locations/ems-expired',
      'daily-truck': '/locations/daily-truck',
      'truck-checkout': '/locations/truck-checkout',
      'non-controlled': '/locations/non-controlled',
      'non-controlled-report': '/non-controlled-report',
      'daily-status': '/status',
      'update-status': '/update-status'
    };

    const route = routeMap[destination];
    if (route) navigate(route);
  };

  if (needsPinReset && location.pathname !== '/set-pin') return null;

  return (
    <Routes>
      <Route
        path="/set-pin"
        element={<SetPin userInfo={userProfile} onComplete={handleProfileComplete} onLogout={handleLogout} />}
      />
      {/* Onboarding / Public Routes */}
      <Route path="/onboarding" element={<Welcome onComplete={handleOnboardingComplete} />} />
      <Route
        path="/signup"
        element={<Signup onSignupSuccess={handleSignupSuccess} onSwitchToLogin={handleSwitchToLogin} />}
      />
      <Route
        path="/confirmation"
        element={<EmailConfirmation userEmail={userEmail} onConfirmed={handleEmailConfirmed} onGoToLogin={handleGoToLogin} />}
      />
      <Route
        path="/login"
        element={<Login onLoginSuccess={handleLoginSuccess} onSwitchToSignup={handleSwitchToSignup} onForgotPassword={handleForgotPassword} />}
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/complete-profile" element={<CompleteProfile onComplete={handleProfileComplete} />} />
      <Route
        path="/forgot-password"
        element={<ForgotPassword onResetRequest={handlePasswordResetRequest} onBackToLogin={handleGoToLogin} />}
      />
      <Route
        path="/password-reset"
        element={<PasswordReset userEmail={userEmail} onResetComplete={handlePasswordResetComplete} />}
      />

      {/* Public Daily Status */}
      <Route path="/status" element={<PublicDailyStatus />} />

      {/* Authenticated Routes */}
      <Route
        path="/home"
        element={<DashboardHome userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/profile"
        element={<Profile userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/daily-dashboard"
        element={<DailyDashboard userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/patient-care-guidelines"
        element={<PatientCareGuidelines userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/patient-weight-converter"
        element={<PatientWeightConverter userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/iv-medication-calculator"
        element={<IVMedicationCalculator userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/glasgow-coma-scale"
        element={<GlasgowComaScale userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/apgar-calculator"
        element={<ApgarCalculator userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/hamilton-t1-duration"
        element={<HamiltonT1Duration userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/phone-directory"
        element={<PhoneDirectory userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />

      {/* Update Daily Status - Auth Required */}
      <Route
        path="/update-status"
        element={<UpdateDailyStatus userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />

      {/* Admin */}
      <Route
        path="/admin-dashboard"
        element={<Admin userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/chain-of-custody-report"
        element={<ChainOfCustodyReport userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/provider-usage-report"
        element={<ProviderUsageReport userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/master-inventory"
        element={<MasterInventory userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/daily-dashboard-admin"
        element={<DailyDashboardAdmin userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/mileage-report"
        element={<MileageReport userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/expiration-report"
        element={<ExpirationReport userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/truck-activity-report"
        element={<TruckActivityReport userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/shift-reconciliation-report"
        element={<ShiftReconciliationReport userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/users"
        element={<Users userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />
      <Route
        path="/non-controlled-report"
        element={<NonControlledReport userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />}
      />

      {/* Locations */}
      <Route path="/locations/mayo21" element={<Mayo21 userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/mayo22" element={<Mayo22 userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/mayo23" element={<Mayo23 userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/mayo24" element={<Mayo24 userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/mayo25" element={<Mayo25 userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/ems-station" element={<EMSStation userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/ems-expired" element={<EMSExpired userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/daily-truck" element={<DailyTruck userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/truck-checkout" element={<TruckCheckOut userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />
      <Route path="/locations/non-controlled" element={<NonControlledMedications userInfo={userProfile} isAdmin={isAdmin} onLogout={handleLogout} onNavigate={handleNavigate} />} />

      {/* Default */}
      <Route path="/" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

// Main App Component with Router wrapper
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
